'use client'

import * as SelectPrimitive from '@radix-ui/react-select'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import * as React from 'react'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

function SelectTrigger({
  className,
  label,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & { label?: string }) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={clsx(
        'backgroundColorBlackToWhite flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-solid border-gray-90 px-4 py-2 font-medium outline-0 transition-colors',
        'focus:border-gray-70 data-placeholder:text-gray-80',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&>span]:line-clamp-1 [&>span]:flex [&>span]:items-center [&>span]:gap-2',
        className
      )}
      {...props}
    >
      {label && <span className="text-gray-90 text-body-3 gap-0.5">{label}</span>}
      <div className="flex w-full items-center justify-between gap-2">
        {children}
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="text-gray-80 -mt-4 size-4 shrink-0 opacity-70" />
        </SelectPrimitive.Icon>
      </div>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        className={clsx(
          'backgroundColorBlackToWhite text-gray-90 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-xl border border-solid border-gray-100 shadow-lg',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={clsx(
            'p-1',
            position === 'popper' && 'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={clsx('text-gray-80 text-body3-regular px-2 py-1.5 font-medium', className)}
      {...props}
    />
  )
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={clsx(
        'text-gray-80 text-body2-regular relative flex w-full cursor-default items-center rounded-lg py-2 pr-2 pl-4 outline-none select-none',
        'focus:bg-gray-100/80 data-disabled:pointer-events-none data-disabled:opacity-50',
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={clsx('-mx-1 my-1 h-px border-t border-solid border-gray-100', className)}
      {...props}
    />
  )
}

export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue }
